import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { ROUTES } from 'src/enums/Routes';

interface ErrorNavigationOptions {
  route?: string;
  replace?: boolean;
  state?: any;
}

/**
 * Custom hook that provides a callback function for navigating to error pages.
 * This hook encapsulates error navigation logic and provides a clean API for error handling.
 *
 * @returns A memoized callback function that navigates to an error page
 *
 * @example
 * ```typescript
 * const redirectToError = useErrorFallback();
 *
 * // Basic usage - navigates to default error page
 * onServerError: redirectToError
 *
 * // With options
 * onServerError: () => redirectToError({
 *   route: '/custom-error',
 *   replace: true,
 *   state: { context: 'billing' }
 * })
 * ```
 */
export const useErrorFallback = () => {
  const navigate = useNavigate();

  return useCallback(
    (options: ErrorNavigationOptions = {}) => {
      const { route = ROUTES.ERROR_PAGE, replace = false, state } = options;
      navigate(route, { replace, state });
    },
    [navigate]
  );
};
